import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Users, Sparkles, BookOpen, Zap, Activity, Mail, ChartLine, Gauge, ShieldCheck, Search, ArrowUpRight, AlertCircle, Loader2, Trash2, TrendingUp, Send, Copy, CheckCircle2, FileArchive, Upload } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import Sparkline from "../components/Sparkline";

const ADMIN_DOMAIN = "@durgaprogrammingadventures.com";
const TIER_LABEL = { free: "Explorer", solo: "Solo", student: "Apprentice", builder: "Creator", studio: "Founder" };

function isAdminEmail(email) {
  return !!email && email.toLowerCase().endsWith(ADMIN_DOMAIN);
}

function fmt(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("en-IN");
}

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function StatCard({ label, value, sub, icon: Icon, tone = "marigold", testid }) {
  const tones = {
    marigold: "bg-saffron/10 text-marigold border-saffron/30",
    teal: "bg-teal/10 text-teal border-teal/30",
    ink: "bg-ink/5 text-ink border-ink/20",
    rose: "bg-rose-50 text-rose-600 border-rose-200",
  };
  return (
    <div className="card-soft p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-widest text-ink2 font-semibold">{label}</div>
        <div className={`w-9 h-9 rounded-xl border ${tones[tone]} flex items-center justify-center`}>
          {Icon && <Icon className="w-4 h-4" />}
        </div>
      </div>
      <div className="font-heading text-3xl font-bold text-ink leading-none">{value}</div>
      {sub && <div className="text-xs text-ink2 mt-2">{sub}</div>}
    </div>
  );
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState({ rows: [], total: 0 });
  const [activity, setActivity] = useState([]);
  const [appBps, setAppBps] = useState({ rows: [], total: 0 });
  const [gameBps, setGameBps] = useState({ rows: [], total: 0 });
  const [lists, setLists] = useState({ newsletter: [], waitlist: [] });
  const [tab, setTab] = useState("overview");
  const [usersQ, setUsersQ] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [reEng, setReEng] = useState(null);
  const [reEngBusy, setReEngBusy] = useState(false);
  const [reEngSelected, setReEngSelected] = useState(null);
  const [reEngCode, setReEngCode] = useState("COMEBACK25");
  const [reEngPercent, setReEngPercent] = useState(25);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [invites, setInvites] = useState({ rows: [], resend_configured: false });
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !isAdminEmail(user.email)) return;
    (async () => {
      try {
        const [ov, us, act, ab, gb, ls, an, inv, re] = await Promise.all([
          api.get("/admin/overview"),
          api.get("/admin/users", { params: { limit: 60 } }),
          api.get("/admin/activity", { params: { limit: 60 } }),
          api.get("/admin/blueprints", { params: { kind: "app", limit: 30 } }),
          api.get("/admin/blueprints", { params: { kind: "game", limit: 30 } }),
          api.get("/admin/lists"),
          api.get("/admin/analytics", { params: { days: 30 } }),
          api.get("/admin/invites"),
          api.get("/admin/re-engagement"),
        ]);
        setOverview(ov.data);
        setUsers(us.data);
        setActivity(act.data.rows || []);
        setAppBps(ab.data);
        setGameBps(gb.data);
        setLists(ls.data);
        setAnalytics(an.data);
        setInvites(inv.data);
        setReEng(re.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Could not load admin data");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const reloadAnalytics = async (days) => {
    setAnalyticsDays(days);
    try {
      const { data } = await api.get("/admin/analytics", { params: { days } });
      setAnalytics(data);
    } catch { toast.error("Could not refresh analytics"); }
  };

  const sendInvite = async (e) => {
    e?.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!email.endsWith(ADMIN_DOMAIN)) {
      toast.error(`Invitee email must end with ${ADMIN_DOMAIN}`);
      return;
    }
    setInviteBusy(true);
    try {
      const { data } = await api.post("/admin/invite", { email, name: inviteName.trim() });
      setLastInviteUrl(data.invite_url);
      if (data.email_sent) {
        toast.success(`Magic link emailed to ${email}`, { description: "Expires in 30 minutes." });
      } else {
        toast.success("Magic link generated", {
          description: data.resend_configured
            ? `Email send failed (${data.email_reason}). Copy the link below.`
            : "Resend isn't configured — copy the link to share manually.",
        });
      }
      setInviteEmail("");
      setInviteName("");
      const { data: list } = await api.get("/admin/invites");
      setInvites(list);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invite failed");
    } finally {
      setInviteBusy(false);
    }
  };

  const copyInviteUrl = (url) => {
    navigator.clipboard.writeText(url).then(
      () => toast.success("Invite link copied"),
      () => toast.error("Could not copy")
    );
  };

  const toggleReEngTarget = (id) => {
    setReEngSelected(prev => {
      const base = prev ?? new Set((reEng?.at_risk || []).map(r => r.id));
      const next = new Set(base);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openCampaign = async (id) => {
    if (expandedCampaign === id) {
      setExpandedCampaign(null); setCampaignDetail(null); return;
    }
    setExpandedCampaign(id);
    setCampaignDetail(null);
    try {
      const { data } = await api.get(`/admin/re-engagement/campaigns/${id}`);
      setCampaignDetail(data);
    } catch { toast.error("Could not load campaign"); }
  };

  const toggleRedemption = async (campaignId, userId) => {
    try {
      const { data } = await api.post("/admin/re-engagement/redeem", {
        campaign_id: campaignId, user_id: userId, source: "manual",
      });
      toast.success(data.redeemed ? "Marked redeemed" : "Redemption cleared");
      const [det, all] = await Promise.all([
        api.get(`/admin/re-engagement/campaigns/${campaignId}`),
        api.get("/admin/re-engagement"),
      ]);
      setCampaignDetail(det.data);
      setReEng(all.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };


  const sendReEngagement = async () => {
    const total = reEng?.at_risk?.length || 0;
    const count = reEngSelected ? reEngSelected.size : total;
    if (count === 0) {
      toast.error("No recipients selected.");
      return;
    }
    if (!window.confirm(
      `Send "${reEngCode}" (${reEngPercent}% off) to ${count} at-risk user${count === 1 ? "" : "s"}?\n` +
      (reEng?.resend_configured
        ? "Emails will go out via Resend."
        : "Resend isn't configured — emails will NOT actually send. The campaign will still be logged.")
    )) return;
    setReEngBusy(true);
    try {
      const body = {
        discount_code: reEngCode.trim().toUpperCase() || "COMEBACK25",
        discount_percent: Number(reEngPercent) || 25,
      };
      if (reEngSelected) body.user_ids = Array.from(reEngSelected);
      const { data } = await api.post("/admin/re-engagement/send", body);
      toast.success(
        data.sent > 0
          ? `Campaign fired · ${data.sent} sent, ${data.failed} failed`
          : `Campaign logged · ${data.targeted} targeted (emails didn't send: ${data.resend_configured ? "check Resend dashboard" : "RESEND_API_KEY missing"})`
      );
      const { data: fresh } = await api.get("/admin/re-engagement");
      setReEng(fresh);
      setReEngSelected(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Campaign failed");
    } finally {
      setReEngBusy(false);
    }
  };

  const refreshUsers = async () => {
    try {
      const { data } = await api.get("/admin/users", { params: { limit: 60, q: usersQ || undefined } });
      setUsers(data);
    } catch { toast.error("Could not refresh users"); }
  };

  const setTier = async (uid, tier) => {
    try {
      await api.post(`/admin/users/${uid}/tier`, { tier });
      toast.success(`Tier set to ${TIER_LABEL[tier]}`);
      refreshUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deleteBp = async (kind, id) => {
    if (!window.confirm("Delete this blueprint?")) return;
    try {
      await api.delete(`/admin/blueprints/${kind}/${id}`);
      toast.success("Deleted");
      const { data } = await api.get("/admin/blueprints", { params: { kind, limit: 30 } });
      if (kind === "app") setAppBps(data); else setGameBps(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // Auth guards
  if (authLoading) {
    return <div className="container-narrow py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-marigold" /></div>;
  }
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!isAdminEmail(user.email)) {
    return (
      <div className="container-narrow py-16 text-center" data-testid="admin-forbidden">
        <AlertCircle className="w-10 h-10 mx-auto text-rose-500 mb-3" />
        <h1 className="font-heading text-3xl font-semibold mb-2">Admin only</h1>
        <p className="text-ink2 max-w-lg mx-auto">This dashboard is restricted to web developers on the <span className="font-mono text-ink">@durgaprogrammingadventures.com</span> domain.</p>
        <Link to="/" className="mt-6 inline-flex btn-primary text-sm py-2">Back to studio</Link>
      </div>
    );
  }

  if (loading) return <div className="container-narrow py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-marigold" /></div>;
  if (error) return <div className="container-narrow py-16 text-center text-rose-600" data-testid="admin-error">{error}</div>;

  const tiers = overview?.users?.tiers || {};
  const credits = overview?.activity?.credits_by_action || {};

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10" data-testid="admin-page">
      <header className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div>
          <div className="label-eyebrow inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Admin · internal</div>
          <h1 className="font-heading text-3xl md:text-5xl font-semibold leading-tight mt-2">Mission control</h1>
          <p className="text-ink2 mt-1 text-sm">Live KPIs, users, activity. Updated on every page load.</p>
        </div>
        <div className="text-xs text-ink2">
          Signed in as <span className="font-mono text-ink">{user.email}</span>
        </div>
      </header>

      {/* Tabs */}
      <nav className="flex flex-wrap gap-2 mb-6 border-b border-border" data-testid="admin-tabs">
        {[
          ["overview", "Overview"],
          ["analytics", "Analytics"],
          ["users", `Users · ${users.total}`],
          ["blueprints", `Blueprints · ${appBps.total + gameBps.total}`],
          ["activity", "Activity"],
          ["invites", `Invites · ${invites.rows?.length || 0}`],
          ["library", "Library"],
          ["tour", "Tour"],
          ["lists", `Lists · ${(lists.newsletter?.length || 0) + (lists.waitlist?.length || 0)}`],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-semibold rounded-t-xl border-b-2 -mb-px transition ${tab === k ? "text-marigold border-marigold" : "text-ink2 border-transparent hover:text-ink"}`}
            data-testid={`admin-tab-${k}`}
          >
            {label}
          </button>
        ))}
      </nav>

      {/* ANALYTICS */}
      {tab === "analytics" && analytics && (
        <div data-testid="admin-analytics">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="font-heading text-2xl font-semibold inline-flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-marigold" /> Last {analyticsDays} days
            </h2>
            <div className="inline-flex bg-paper border border-border rounded-full p-1" data-testid="analytics-range">
              {[7, 30, 90].map(d => (
                <button
                  key={d}
                  onClick={() => reloadAnalytics(d)}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition ${analyticsDays === d ? "bg-white shadow-sm text-ink" : "text-ink2"}`}
                  data-testid={`analytics-range-${d}`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              ["Signups", analytics.totals.signups, analytics.series.signups, "#E0761F", "rgba(224,118,31,.16)"],
              ["Blueprints", analytics.totals.blueprints, analytics.series.blueprints, "#2A7D7B", "rgba(42,125,123,.16)"],
              ["Credits", analytics.totals.credits, analytics.series.credits, "#1F1A17", "rgba(31,26,23,.16)"],
              ["Public shares", analytics.totals.shares, analytics.series.shares, "#F4A73A", "rgba(244,167,58,.18)"],
            ].map(([label, total, series, stroke, fill]) => (
              <div key={label} className="card-soft p-4" data-testid={`analytics-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-widest text-ink2 font-semibold">{label}</div>
                  <div className="font-heading text-2xl font-bold tabular-nums">{fmt(total)}</div>
                </div>
                <Sparkline data={series} stroke={stroke} fill={fill} height={48} />
              </div>
            ))}
          </div>

          {/* Re-engagement campaign */}
          {reEng && (
            <div
              className="card-soft p-6 mb-6 bg-gradient-to-br from-rose-50 via-cream to-saffron/10 border-2 border-saffron/40"
              data-testid="re-engagement-card"
            >
              <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6">
                <div>
                  <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-rose-600 mb-2">
                    <AlertCircle className="w-3.5 h-3.5" /> Churn risk · {reEng.last_month}
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-semibold mb-2 leading-tight">
                    {reEng.at_risk_count === 0 ? (
                      <>No at-risk users right now 🎉</>
                    ) : (
                      <>
                        <span className="tabular-nums text-marigold">{reEng.at_risk_count}</span> user{reEng.at_risk_count === 1 ? "" : "s"} hit their cap last month and haven't returned this month.
                      </>
                    )}
                  </h3>
                  <p className="text-sm text-ink2 mb-4 max-w-xl">
                    Fire a one-click email blast offering a Prompt Booster discount. Same Resend pipeline as admin invites. Discount code is included in the email; you'll wire the actual redemption once Stripe lands.
                  </p>

                  {reEng.at_risk_count > 0 && (
                    <>
                      <div className="flex flex-wrap items-end gap-3 mb-4">
                        <div>
                          <label className="block text-[10px] uppercase tracking-widest text-ink2 font-semibold mb-1">Discount code</label>
                          <input
                            value={reEngCode}
                            onChange={e => setReEngCode(e.target.value.toUpperCase())}
                            maxLength={20}
                            className="px-3 py-2 rounded-xl border-2 border-border focus:border-ink bg-paper font-mono text-sm w-40 outline-none"
                            data-testid="re-eng-code-input"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-widest text-ink2 font-semibold mb-1">% off</label>
                          <input
                            type="number"
                            min={1}
                            max={90}
                            value={reEngPercent}
                            onChange={e => setReEngPercent(e.target.value)}
                            className="px-3 py-2 rounded-xl border-2 border-border focus:border-ink bg-paper font-semibold text-sm w-20 tabular-nums outline-none"
                            data-testid="re-eng-percent-input"
                          />
                        </div>
                        <button
                          onClick={sendReEngagement}
                          disabled={reEngBusy}
                          className="btn-primary text-sm py-2 disabled:opacity-50"
                          data-testid="re-eng-send-button"
                        >
                          {reEngBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          Send to {reEngSelected ? reEngSelected.size : reEng.at_risk_count} user{(reEngSelected ? reEngSelected.size : reEng.at_risk_count) === 1 ? "" : "s"}
                        </button>
                      </div>
                      {!reEng.resend_configured && (
                        <div className="text-[11px] text-ink2 bg-paper border border-border rounded-xl p-2 mb-3" data-testid="re-eng-resend-warning">
                          <strong>Heads up:</strong> Resend isn't configured — campaigns are logged but emails won't actually send until <span className="font-mono">RESEND_API_KEY</span> is set.
                        </div>
                      )}
                      {reEngSelected && (
                        <button
                          type="button"
                          onClick={() => setReEngSelected(null)}
                          className="text-xs text-marigold hover:underline"
                          data-testid="re-eng-select-all"
                        >
                          Selecting {reEngSelected.size} · click to select all {reEng.at_risk_count}
                        </button>
                      )}
                    </>
                  )}

                  {reEng.campaigns && reEng.campaigns.length > 0 && (
                    <div className="mt-6" data-testid="past-campaigns-section">
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <div className="text-[10px] uppercase tracking-widest text-ink2 font-bold">Past campaigns · recovery tracking</div>
                        {reEng.recovery_totals && reEng.recovery_totals.targeted > 0 && (
                          <div className="text-[11px] text-ink2" data-testid="recovery-totals">
                            <span className="font-semibold text-teal">{reEng.recovery_totals.redeemed}</span>
                            <span className="text-ink2">/{reEng.recovery_totals.targeted} redeemed</span>
                            <span className="mx-2 text-ink2">·</span>
                            <span className="font-semibold text-marigold">{Math.round(reEng.recovery_totals.recovery_rate * 100)}%</span>
                            <span className="text-ink2"> recovery</span>
                            <span className="mx-2 text-ink2">·</span>
                            <span className="font-semibold text-ink tabular-nums">₹{reEng.recovery_totals.recovered_inr.toLocaleString("en-IN")}</span>
                            <span className="text-ink2"> saved</span>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        {reEng.campaigns.slice(0, 6).map(c => {
                          const isOpen = expandedCampaign === c.id;
                          return (
                            <div key={c.id} className="border border-border rounded-xl bg-paper/40" data-testid={`campaign-${c.id}`}>
                              <button
                                type="button"
                                onClick={() => openCampaign(c.id)}
                                className="w-full text-left px-3 py-2 hover:bg-paper/70 rounded-xl transition flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
                                data-testid={`campaign-toggle-${c.id}`}
                              >
                                <span className="font-mono font-bold text-marigold">{c.discount_code}</span>
                                <span className="text-ink2">{c.discount_percent}% off</span>
                                <span className="text-ink2">·</span>
                                <span className="tabular-nums">{c.sent_count}/{c.target_count} sent</span>
                                {c.failed_count > 0 && <span className="text-rose-600">· {c.failed_count} failed</span>}
                                <span className="text-ink2">·</span>
                                <span className="tabular-nums font-semibold text-teal" data-testid={`campaign-recovery-${c.id}`}>
                                  {c.redemptions_count}/{c.target_count} redeemed
                                </span>
                                <span className="tabular-nums font-semibold text-marigold">{Math.round((c.recovery_rate || 0) * 100)}%</span>
                                {c.estimated_recovered_inr > 0 && (
                                  <span className="tabular-nums font-semibold text-ink">₹{c.estimated_recovered_inr.toLocaleString("en-IN")}</span>
                                )}
                                <span className="text-ink2 ml-auto">{timeAgo(c.created_at)}</span>
                              </button>
                              {isOpen && (
                                <div className="border-t border-border p-3 bg-white rounded-b-xl" data-testid={`campaign-detail-${c.id}`}>
                                  {!campaignDetail ? (
                                    <div className="text-xs text-ink2 py-2 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
                                  ) : (
                                    <>
                                      <div className="text-[10px] uppercase tracking-widest text-ink2 font-bold mb-2">
                                        Targets — check the box when a user redeems
                                      </div>
                                      <div className="space-y-1 max-h-56 overflow-y-auto">
                                        {(campaignDetail.target_snapshots || []).map(t => (
                                          <label key={t.user_id} className={`flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-paper/60 cursor-pointer text-xs ${t.redeemed ? "opacity-100" : "opacity-90"}`} data-testid={`target-${t.user_id}`}>
                                            <input
                                              type="checkbox"
                                              checked={!!t.redeemed}
                                              onChange={() => toggleRedemption(c.id, t.user_id)}
                                              className="accent-teal"
                                              data-testid={`target-check-${t.user_id}`}
                                            />
                                            <span className="font-mono flex-1 truncate">{t.email}</span>
                                            <span className="text-ink2">{TIER_LABEL[t.tier] || t.tier}</span>
                                            <span className="text-ink2 tabular-nums">{t.last_month_usage}/{t.last_month_cap}</span>
                                            {t.redeemed && <span className="text-[10px] uppercase font-bold tracking-wider text-teal">Redeemed</span>}
                                          </label>
                                        ))}
                                        {(!campaignDetail.target_snapshots || campaignDetail.target_snapshots.length === 0) && (
                                          <div className="text-xs text-ink2 p-2">Old campaign — target list not stored.</div>
                                        )}
                                      </div>
                                      <div className="text-[11px] text-ink2 mt-3 border-t border-border pt-2">
                                        Once Stripe is wired, its webhook can call <span className="font-mono">POST /api/admin/re-engagement/redeem</span> with <span className="font-mono">source: "stripe_webhook"</span> to auto-check these.
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="card-soft bg-white overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-border text-[11px] uppercase tracking-widest text-ink2 font-bold">At-risk recipients</div>
                  <div className="max-h-[360px] overflow-y-auto divide-y divide-border">
                    {(reEng.at_risk || []).length === 0 && (
                      <div className="p-6 text-center text-sm text-ink2">No users hit their cap last month.</div>
                    )}
                    {(reEng.at_risk || []).map(r => {
                      const selected = reEngSelected ? reEngSelected.has(r.id) : true;
                      return (
                        <label key={r.id} className={`flex items-center gap-3 p-3 hover:bg-paper/50 cursor-pointer ${selected ? "" : "opacity-50"}`} data-testid={`re-eng-row-${r.id}`}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleReEngTarget(r.id)}
                            className="accent-marigold"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm truncate">{r.name || "—"}</div>
                            <div className="text-xs font-mono text-ink2 truncate">{r.email}</div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-600">{Math.round(r.utilization * 100)}%</div>
                            <div className="text-[11px] text-ink2 tabular-nums">{r.last_month_usage}/{r.last_month_cap} · {TIER_LABEL[r.tier] || r.tier}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-5">
            <div className="card-soft overflow-hidden">
              <div className="px-5 py-3 border-b border-border">
                <h3 className="font-heading text-lg font-semibold">Top builders</h3>
                <p className="text-xs text-ink2">By blueprint count in window</p>
              </div>
              <div className="divide-y divide-border max-h-[400px] overflow-y-auto">
                {analytics.top_builders.length === 0 && <div className="p-6 text-center text-sm text-ink2">No activity yet.</div>}
                {analytics.top_builders.map((u, i) => (
                  <div key={u.id || i} className="p-3 flex items-center justify-between hover:bg-paper/40">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-paper text-ink2 text-xs font-mono flex items-center justify-center">{i + 1}</div>
                      <div>
                        <div className="font-semibold text-sm">{u.name || "—"}</div>
                        <div className="text-xs text-ink2 font-mono">{u.email}</div>
                      </div>
                    </div>
                    <div className="text-sm font-semibold tabular-nums text-marigold">{u.blueprint_count} bp</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-soft overflow-hidden">
              <div className="px-5 py-3 border-b border-border">
                <h3 className="font-heading text-lg font-semibold">Top public blueprints</h3>
                <p className="text-xs text-ink2">By refinement count</p>
              </div>
              <div className="divide-y divide-border max-h-[400px] overflow-y-auto">
                {[...(analytics.top_apps || []).map(b => ({ ...b, kind: "app" })),
                  ...(analytics.top_games || []).map(b => ({ ...b, kind: "game" }))]
                  .sort((a, b) => (b.refines || 0) - (a.refines || 0))
                  .slice(0, 12)
                  .map((b, i) => (
                  <Link key={b.id || i} to={`/preview/${b.id}`} target="_blank" className="block p-3 hover:bg-paper/40">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{b.title}</div>
                        <div className="text-xs text-ink2 truncate">
                          {b.author_name || "anon"} · {b.kind}{b.engine ? ` · ${b.engine}` : ""}
                        </div>
                      </div>
                      <div className="text-xs font-semibold tabular-nums text-marigold whitespace-nowrap">{b.refines || 0} refines</div>
                    </div>
                  </Link>
                ))}
                {(!analytics.top_apps?.length && !analytics.top_games?.length) && (
                  <div className="p-6 text-center text-sm text-ink2">No public blueprints yet.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* INVITES — magic-link teammates */}
      {tab === "invites" && (
        <div data-testid="admin-invites" className="grid lg:grid-cols-[1fr_1.2fr] gap-5">
          <form onSubmit={sendInvite} className="card-soft p-6" data-testid="invite-form">
            <h3 className="font-heading text-xl font-semibold mb-1 inline-flex items-center gap-2"><Send className="w-5 h-5 text-marigold" /> Invite a teammate</h3>
            <p className="text-sm text-ink2 mb-4">
              Magic link valid for <span className="font-semibold text-ink">30 minutes</span>, single-use.
              Only <span className="font-mono">@durgaprogrammingadventures.com</span> emails accepted.
            </p>
            {!invites.resend_configured && (
              <div className="text-xs bg-saffron/15 border border-saffron rounded-xl p-3 mb-4 text-ink2" data-testid="resend-not-configured">
                <strong>Heads up:</strong> <span className="font-mono">RESEND_API_KEY</span> isn't set in <span className="font-mono">backend/.env</span> — emails won't send. The link still works; just copy it from the list and share manually. Add the key, restart, and emails kick in instantly.
              </div>
            )}
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink2 mb-1">Name (optional)</label>
            <input
              value={inviteName}
              onChange={e => setInviteName(e.target.value)}
              placeholder="Aria Verma"
              className="w-full mb-3 px-4 py-2.5 rounded-xl border-2 border-border focus:border-ink bg-paper outline-none"
              data-testid="invite-name-input"
            />
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink2 mb-1">Email</label>
            <input
              required
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="aria@durgaprogrammingadventures.com"
              className="w-full mb-4 px-4 py-2.5 rounded-xl border-2 border-border focus:border-ink bg-paper outline-none font-mono text-sm"
              data-testid="invite-email-input"
            />
            <button
              type="submit"
              disabled={inviteBusy}
              className="btn-primary text-sm py-2 disabled:opacity-50"
              data-testid="invite-send-button"
            >
              {inviteBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {inviteBusy ? "Sending…" : "Send magic link"}
            </button>

            {lastInviteUrl && (
              <div className="mt-5 bg-paper border-2 border-ink rounded-xl p-3" data-testid="invite-last-url">
                <div className="text-[11px] uppercase tracking-widest font-bold text-marigold mb-1.5 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Latest invite link
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-[11px] flex-1 truncate font-mono text-ink2">{lastInviteUrl}</code>
                  <button
                    type="button"
                    onClick={() => copyInviteUrl(lastInviteUrl)}
                    className="text-marigold hover:bg-marigold/10 p-1.5 rounded"
                    data-testid="invite-last-copy"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </form>

          <div className="card-soft overflow-hidden">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Recent invites</h3>
              <span className="text-xs text-ink2">{invites.rows?.length || 0} total</span>
            </div>
            <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
              {(invites.rows || []).map(inv => (
                <div key={inv.id} className="p-4 hover:bg-paper/40" data-testid={`invite-row-${inv.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="font-mono text-sm truncate">{inv.email}</div>
                    {inv.email_sent ? (
                      <span className="text-[10px] uppercase font-bold tracking-wider text-teal">Sent</span>
                    ) : (
                      <span className="text-[10px] uppercase font-bold tracking-wider text-marigold">Manual</span>
                    )}
                  </div>
                  <div className="text-xs text-ink2 flex flex-wrap gap-x-3">
                    <span>by {inv.invited_by_email}</span>
                    <span>· {timeAgo(inv.created_at)}</span>
                    {inv.email_reason && <span>· {inv.email_reason}</span>}
                  </div>
                </div>
              ))}
              {(!invites.rows || invites.rows.length === 0) && (
                <div className="p-6 text-center text-sm text-ink2">No invites yet. Send one →</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div data-testid="admin-overview">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total users" value={fmt(overview?.users.total)} sub={`+${overview?.users.signups_7d} this week`} icon={Users} tone="marigold" testid="stat-users" />
            <StatCard label="Prompts this month" value={fmt(overview?.activity.prompts_this_month)} sub={`${overview?.activity.active_users_this_month} active builders`} icon={Sparkles} tone="teal" testid="stat-prompts" />
            <StatCard label="App blueprints" value={fmt(overview?.blueprints.apps_total)} sub={`${overview?.blueprints.apps_public} shared publicly`} icon={ChartLine} tone="ink" testid="stat-apps" />
            <StatCard label="Game blueprints" value={fmt(overview?.blueprints.games_total)} sub={`${overview?.blueprints.games_public} shared publicly`} icon={Gauge} tone="marigold" testid="stat-games" />
          </div>

          <div className="grid lg:grid-cols-3 gap-5 mb-8">
            <div className="card-soft p-5 lg:col-span-2">
              <h3 className="font-heading text-lg font-semibold mb-4 inline-flex items-center gap-2"><Zap className="w-4 h-4 text-marigold" /> Credits this month <span className="text-sm font-normal text-ink2">· {overview?.activity.credits_this_month} units</span></h3>
              <div className="space-y-2">
                {Object.entries(credits).length === 0 && <div className="text-sm text-ink2">No activity yet this month.</div>}
                {Object.entries(credits).sort((a, b) => b[1].cost - a[1].cost).map(([action, info]) => (
                  <div key={action} className="flex items-center gap-3" data-testid={`credit-${action}`}>
                    <div className="text-sm font-mono text-ink w-40">{action}</div>
                    <div className="flex-1 h-2 rounded-full bg-paper overflow-hidden">
                      <div className="h-full bg-marigold" style={{ width: `${Math.min(100, (info.cost / Math.max(1, overview.activity.credits_this_month)) * 100)}%` }} />
                    </div>
                    <div className="text-sm text-ink2 tabular-nums w-24 text-right">{info.count}× · {info.cost} cu</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-soft p-5">
              <h3 className="font-heading text-lg font-semibold mb-4 inline-flex items-center gap-2"><Users className="w-4 h-4 text-teal" /> Tier breakdown</h3>
              <div className="space-y-2">
                {["free", "solo", "student", "builder", "studio"].map(t => (
                  <div key={t} className="flex items-center justify-between text-sm" data-testid={`tier-row-${t}`}>
                    <span className="text-ink2">{TIER_LABEL[t]}</span>
                    <span className="font-mono font-semibold text-ink">{tiers[t] || 0}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-border space-y-1 text-xs text-ink2">
                <div className="flex justify-between"><span>Newsletter</span><span className="text-ink">{fmt(overview?.list.newsletter_subscribers)}</span></div>
                <div className="flex justify-between"><span>Book waitlist</span><span className="text-ink">{fmt(overview?.list.waitlist_subscribers)}</span></div>
                <div className="flex justify-between"><span>GitHub connected</span><span className="text-ink">{fmt(overview?.users.github_connected)}</span></div>
                <div className="flex justify-between"><span>Lesson completions</span><span className="text-ink">{fmt(overview?.list.lesson_completions)}</span></div>
                <div className="flex justify-between"><span>Uploads</span><span className="text-ink">{fmt(overview?.list.uploads)}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* USERS */}
      {tab === "users" && (
        <div data-testid="admin-users">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink2" />
              <input
                value={usersQ}
                onChange={e => setUsersQ(e.target.value)}
                onKeyDown={e => e.key === "Enter" && refreshUsers()}
                placeholder="Search by email or name…"
                className="w-full pl-9 pr-3 py-2 rounded-xl border-2 border-border focus:border-ink bg-paper text-sm outline-none"
                data-testid="admin-users-search"
              />
            </div>
            <button onClick={refreshUsers} className="btn-outline-ink text-sm py-2" data-testid="admin-users-refresh">Search</button>
          </div>
          <div className="card-soft overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-paper border-b border-border">
                <tr className="text-left text-[11px] uppercase tracking-wider text-ink2">
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Tier</th>
                  <th className="px-4 py-3 text-right">Apps</th>
                  <th className="px-4 py-3 text-right">Games</th>
                  <th className="px-4 py-3 text-right">Prompts/mo</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.rows.map(u => (
                  <tr key={u.id} className="border-b border-border last:border-0 hover:bg-paper/50" data-testid={`admin-user-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-ink">{u.name || "—"}</div>
                      <div className="text-xs text-ink2 font-mono">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={u.tier}
                        onChange={e => setTier(u.id, e.target.value)}
                        className="bg-paper border border-border rounded-full px-2 py-1 text-xs"
                        data-testid={`admin-tier-${u.id}`}
                      >
                        {Object.entries(TIER_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{u.blueprints}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{u.games}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{u.prompts_this_month}</td>
                    <td className="px-4 py-3 text-xs text-ink2">{timeAgo(u.created_at)}</td>
                    <td className="px-4 py-3">
                      {u.github_login && <span className="text-[10px] uppercase font-bold text-teal">GH</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* BLUEPRINTS */}
      {tab === "blueprints" && (
        <div className="grid lg:grid-cols-2 gap-5" data-testid="admin-blueprints">
          {[["App", appBps, "app"], ["Game", gameBps, "game"]].map(([label, set, kind]) => (
            <div key={kind} className="card-soft overflow-hidden">
              <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                <h3 className="font-heading text-lg font-semibold">{label} blueprints · {set.total}</h3>
              </div>
              <div className="divide-y divide-border max-h-[640px] overflow-y-auto">
                {set.rows.map(b => (
                  <div key={b.id} className="p-4 hover:bg-paper/40 group" data-testid={`admin-bp-${b.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-ink truncate">{b.title}</span>
                          {b.is_public && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal/15 text-teal">Public</span>}
                          {b.engine && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-marigold/15 text-marigold">{b.engine}</span>}
                          {b.refine_turns_count > 0 && <span className="text-[10px] text-ink2">·{b.refine_turns_count} refines</span>}
                          {b.branched_from && <span className="text-[10px] text-ink2">· branch</span>}
                          {b.remixed_from && <span className="text-[10px] text-ink2">· remix</span>}
                        </div>
                        <div className="text-xs text-ink2 mt-1 line-clamp-1">{b.concept}</div>
                        <div className="text-[11px] text-ink2 mt-1">
                          {b.author_name || "anon"} · {timeAgo(b.created_at)}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {b.is_public && (
                          <Link to={`/preview/${b.id}`} target="_blank" className="text-xs inline-flex items-center gap-1 text-marigold hover:underline">
                            View <ArrowUpRight className="w-3 h-3" />
                          </Link>
                        )}
                        <button onClick={() => deleteBp(kind, b.id)} className="text-xs text-rose-500 opacity-0 group-hover:opacity-100 transition inline-flex items-center gap-1" data-testid={`admin-bp-delete-${b.id}`}>
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {set.rows.length === 0 && <div className="p-6 text-center text-sm text-ink2">None yet.</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ACTIVITY */}
      {tab === "activity" && (
        <div className="card-soft overflow-hidden" data-testid="admin-activity">
          <div className="divide-y divide-border max-h-[700px] overflow-y-auto">
            {activity.length === 0 && <div className="p-6 text-center text-sm text-ink2">No activity yet.</div>}
            {activity.map((a, i) => (
              <div key={i} className="p-4 flex items-center gap-4 hover:bg-paper/40">
                <Activity className="w-4 h-4 text-marigold flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-ink">
                    <span className="font-semibold">{a.action}</span> · {a.cost} cu
                  </div>
                  <div className="text-xs text-ink2">
                    {a.email || a.user_id?.slice(0, 8)} {a.tier && <span className="ml-1 px-1.5 rounded bg-paper text-[10px]">{TIER_LABEL[a.tier] || a.tier}</span>}
                    {a.meta && a.meta.title && <> · "{a.meta.title}"</>}
                    {a.meta && a.meta.refine && <> · refinement</>}
                  </div>
                </div>
                <div className="text-xs text-ink2 whitespace-nowrap">{timeAgo(a.at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LIBRARY (zip imports) */}
      {tab === "library" && <LibraryTab />}

      {/* TOUR (concept constellation author mode) */}
      {tab === "tour" && <ConceptTourTab />}

      {/* LISTS */}
      {tab === "lists" && (
        <div className="grid md:grid-cols-2 gap-5" data-testid="admin-lists">
          {[["Newsletter", lists.newsletter, Mail], ["Book waitlist", lists.waitlist, BookOpen]].map(([label, list, Icon]) => (
            <div key={label} className="card-soft overflow-hidden">
              <div className="px-5 py-3 border-b border-border flex items-center gap-2">
                <Icon className="w-4 h-4 text-marigold" />
                <h3 className="font-heading text-lg font-semibold">{label}</h3>
                <span className="text-sm text-ink2">· {list?.length || 0}</span>
                <button
                  onClick={() => {
                    const csv = ["email,created_at,book_id"].concat((list || []).map(r => `${r.email},${r.created_at || ""},${r.book_id || ""}`)).join("\n");
                    navigator.clipboard.writeText(csv).then(() => toast.success(`${label} CSV copied`));
                  }}
                  className="ml-auto text-xs text-marigold hover:underline"
                  data-testid={`admin-list-export-${label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  Copy CSV
                </button>
              </div>
              <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
                {(list || []).map((s, i) => (
                  <div key={i} className="p-3 flex items-center justify-between text-sm hover:bg-paper/40">
                    <div>
                      <div className="font-mono">{s.email}</div>
                      {s.book_id && <div className="text-xs text-ink2">{s.book_id}</div>}
                    </div>
                    <div className="text-xs text-ink2">{timeAgo(s.created_at)}</div>
                  </div>
                ))}
                {(!list || list.length === 0) && <div className="p-6 text-center text-sm text-ink2">Empty.</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─────────────────────── Library (zip imports) tab ──────────────────────
function LibraryTab() {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [poller, setPoller] = useState(null);
  // Knowledge-Base (PDF-zip) uploader state
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfBookCode, setPdfBookCode] = useState("KB01");
  const [pdfUploading, setPdfUploading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/lesson-imports")
      .then(({ data }) => setImports(data.imports || []))
      .catch(() => setImports([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Auto-refresh while any job is still parsing.
  useEffect(() => {
    const anyPending = imports.some((i) => i.status === "pending" || i.status === "parsing");
    if (anyPending && !poller) {
      const p = setInterval(load, 2000);
      setPoller(p);
      return () => clearInterval(p);
    }
    if (!anyPending && poller) {
      clearInterval(poller);
      setPoller(null);
    }
  }, [imports, poller]);

  const upload = async () => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please choose a .zip file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/lesson-imports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${data.filename} — parsing…`);
      setFile(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this import and all its articles?")) return;
    try {
      await api.delete(`/admin/lesson-imports/${id}`, { data: { also_delete_articles: true } });
      toast.success("Import deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const uploadPdfKb = async () => {
    if (!pdfFile) return;
    if (!pdfFile.name.toLowerCase().endsWith(".zip")) {
      toast.error("Knowledge-Base uploads must be a .zip of PDFs");
      return;
    }
    const code = (pdfBookCode || "KB01").trim().toUpperCase();
    if (!/^[A-Z]{1,4}\d{1,3}$/.test(code)) {
      toast.error("Book code must be alphanumeric (e.g. KB01, KB02)");
      return;
    }
    setPdfUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pdfFile);
      fd.append("book_code", code);
      const { data } = await api.post("/admin/lesson-imports/pdf-kb", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${data.filename} → ${code} · parsing…`);
      setPdfFile(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setPdfUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-library">
      {/* Upload card */}
      <div className="card-soft p-5" data-testid="admin-library-upload">
        <div className="flex items-center gap-2 mb-3">
          <FileArchive className="w-4 h-4 text-marigold" />
          <h3 className="font-heading text-lg font-semibold">Import Markdown zip</h3>
        </div>
        <p className="text-sm text-ink2 mb-4">
          Drop a <code className="font-mono text-xs bg-cream px-1 rounded">.zip</code> of nested folders containing Markdown files.
          Folder names become categories.  YAML frontmatter (<code className="font-mono text-xs bg-cream px-1 rounded">--- title: ... ---</code>) is honoured; otherwise the first <code className="font-mono text-xs bg-cream px-1 rounded">#</code> heading or the filename becomes the title.
          Max 40 MB per zip, 1 MB per file.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".zip,application/zip"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-xs"
            data-testid="admin-library-file"
          />
          <button
            onClick={upload}
            disabled={uploading || !file}
            className="btn-primary text-xs inline-flex items-center gap-2"
            data-testid="admin-library-upload-btn"
          >
            {uploading ? <><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</> : <><Upload className="w-3 h-3" /> Upload &amp; parse</>}
          </button>
          {file && <div className="text-xs text-ink2 font-mono">{file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB</div>}
        </div>
      </div>

      {/* Knowledge Base (PDF zip) upload card */}
      <div className="card-soft p-5 border-2 border-royal/20" data-testid="admin-library-upload-pdfkb">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-4 h-4 text-royal" />
          <h3 className="font-heading text-lg font-semibold">Import Knowledge Base (PDF zip)</h3>
          <span className="text-[10px] uppercase tracking-widest font-bold text-royal bg-gold border border-royal rounded-full px-2 py-0.5">New</span>
        </div>
        <p className="text-sm text-ink2 mb-4">
          Drop a <code className="font-mono text-xs bg-cream px-1 rounded">.zip</code> of PDFs (e.g. the 52-part curriculum bundle). Each PDF is split by <code className="font-mono text-xs bg-cream px-1 rounded">CS-Bxx-Mxx-Axx</code> code headers into individual Library articles. Rewrites the incoming book code to your target below, so it lands as a fresh book.
          Max 120 MB per zip.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".zip,application/zip"
            onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
            className="text-xs"
            data-testid="admin-library-pdfkb-file"
          />
          <label className="text-xs text-ink2 inline-flex items-center gap-2">
            Book code
            <input
              type="text"
              value={pdfBookCode}
              onChange={(e) => setPdfBookCode(e.target.value.toUpperCase())}
              placeholder="KB01"
              maxLength={7}
              className="w-20 px-2 py-1 rounded border-2 border-royal/30 bg-cream text-xs font-mono uppercase"
              data-testid="admin-library-pdfkb-book"
            />
          </label>
          <button
            onClick={uploadPdfKb}
            disabled={pdfUploading || !pdfFile}
            className="btn-royal text-xs inline-flex items-center gap-2"
            data-testid="admin-library-pdfkb-btn"
          >
            {pdfUploading ? <><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</> : <><Upload className="w-3 h-3" /> Upload &amp; parse</>}
          </button>
          {pdfFile && <div className="text-xs text-ink2 font-mono">{pdfFile.name} · {(pdfFile.size / 1024 / 1024).toFixed(2)} MB</div>}
        </div>
      </div>

      {/* History list */}
      <div className="card-soft overflow-hidden" data-testid="admin-library-imports">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2">
          <h3 className="font-heading text-lg font-semibold">Import history</h3>
          <span className="text-sm text-ink2">· {imports.length}</span>
          {poller && <span className="ml-auto text-[10px] font-mono text-marigold inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> auto-refresh</span>}
        </div>
        {loading && <div className="p-6 text-sm text-ink2 text-center">Loading…</div>}
        {!loading && imports.length === 0 && (
          <div className="p-6 text-center text-sm text-ink2 italic">No imports yet. Upload a zip above to get started.</div>
        )}
        <div className="divide-y divide-border">
          {imports.map((row) => (
            <div key={row.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`admin-library-import-${row.id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="font-mono text-sm truncate">{row.filename}</div>
                <div className="text-xs text-ink2">
                  <span className={`inline-block px-2 py-0.5 rounded-full mr-2 text-[10px] uppercase tracking-widest font-bold ${
                    row.status === "ready" ? "bg-teal/20 text-teal" :
                    row.status === "failed" ? "bg-[#C1666B]/20 text-[#C1666B]" :
                    "bg-marigold/20 text-marigold"
                  }`} data-testid={`admin-library-status-${row.id}`}>{row.status}</span>
                  {row.kind && (
                    <span className={`inline-block px-2 py-0.5 rounded-full mr-2 text-[10px] uppercase tracking-widest font-bold ${
                      row.kind === "pdf-kb" ? "bg-royal/15 text-royal" :
                      row.kind === "epub" ? "bg-gold/25 text-royal" :
                      "bg-ink/10 text-ink2"
                    }`}>{row.kind}{row.target_book_code ? ` · ${row.target_book_code}` : ""}</span>
                  )}
                  {(row.size / 1024 / 1024).toFixed(2)} MB
                  {" · "}{new Date(row.created_at).toLocaleString()}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center text-xs" data-testid={`admin-library-stats-${row.id}`}>
                <div>
                  <div className="font-heading text-lg text-marigold">{row.parsed_count || 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-ink2">Parsed</div>
                </div>
                <div>
                  <div className="font-heading text-lg text-ink2">{row.skipped_count || 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-ink2">Skipped</div>
                </div>
                <div>
                  <div className="font-heading text-lg text-ink2">{(row.categories || []).length}</div>
                  <div className="text-[10px] uppercase tracking-widest text-ink2">Categories</div>
                </div>
              </div>
              <button
                onClick={() => remove(row.id)}
                className="text-[#C1666B] hover:text-[#C1666B]/80 text-xs inline-flex items-center gap-1"
                data-testid={`admin-library-delete-${row.id}`}
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
              {row.error && (
                <div className="w-full text-xs text-[#C1666B] bg-[#C1666B]/10 border border-[#C1666B]/30 rounded-md px-3 py-2 mt-1" data-testid={`admin-library-error-${row.id}`}>
                  {row.error}
                </div>
              )}
              {row.categories && row.categories.length > 0 && (
                <div className="w-full flex flex-wrap gap-1.5 pt-1">
                  {row.categories.map((c) => (
                    <span key={c} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-cream border border-ink/20 text-ink2">{c}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-border text-xs">
          <Link to="/library" className="text-marigold hover:underline inline-flex items-center gap-1" data-testid="admin-library-view-public">
            View the public library <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}


function ConceptTourTab() {
  const [tours, setTours] = useState([]);
  const [live, setLive] = useState(null);          // currently active tour from public endpoint
  const [title, setTitle] = useState("");
  const [intro, setIntro] = useState("");
  const [stops, setStops] = useState([
    { term: "", caption: "" },
    { term: "", caption: "" },
    { term: "", caption: "" },
    { term: "", caption: "" },
    { term: "", caption: "" },
  ]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/admin/concept-tour").catch(() => ({ data: { tours: [] } })),
      api.get("/curriculum/concept-tour").catch(() => ({ data: null })),
    ]).then(([a, b]) => {
      setTours(a.data.tours || []);
      setLive(b.data);
      const activeAdmin = (a.data.tours || []).find((t) => t.active);
      if (activeAdmin) {
        setTitle(activeAdmin.title || "");
        setIntro(activeAdmin.intro || "");
        const padded = [...(activeAdmin.stops || [])];
        while (padded.length < 5) padded.push({ term: "", caption: "" });
        setStops(padded.slice(0, 5));
      }
    });
  };
  useEffect(() => { load(); }, []);

  const setStop = (i, field, value) => {
    setStops((prev) => prev.map((s, j) => j === i ? { ...s, [field]: value } : s));
  };

  const publish = async () => {
    const clean = stops
      .map((s) => ({ term: (s.term || "").trim().toLowerCase(), caption: (s.caption || "").trim() }))
      .filter((s) => s.term.length >= 3 && s.caption.length >= 6);
    if (clean.length < 3) {
      toast.error("Need at least 3 complete stops (term + caption).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/concept-tour", { title: title.trim() || null, intro: intro.trim() || null, stops: clean });
      toast.success("Tour published — new visitors will see this first.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't publish the tour.");
    } finally {
      setBusy(false);
    }
  };

  const revert = async () => {
    setBusy(true);
    try {
      await api.post("/admin/concept-tour/reset");
      toast.success("Reverted to Ms. Pradhan's default tour.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't revert.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-soft p-6" data-testid="admin-tour">
      <div className="flex items-start gap-3 flex-wrap mb-5">
        <h2 className="font-heading text-2xl font-semibold flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-marigold" /> Concept constellation tour
        </h2>
        <div className="ml-auto text-xs text-ink2">
          {live && (
            <span data-testid="admin-tour-live-source">
              Currently live: <span className="font-mono uppercase tracking-widest text-marigold">{live.curated_by}</span>
            </span>
          )}
        </div>
      </div>

      <p className="text-sm text-ink2 mb-5 max-w-2xl">
        Ms. Pradhan's default tour is five hand-written stops. Publish a different tour here and every new visitor to <code>/learn/graph</code> in Concepts mode sees your version first. Terms must appear on the concept graph (see the sidebar). Empty rows are ignored.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <label className="block">
          <span className="text-xs text-ink2 uppercase tracking-widest font-bold">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 140))}
            placeholder="e.g. This week: small ideas that scale"
            className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm font-heading focus:outline-none focus:border-marigold"
            data-testid="admin-tour-title"
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink2 uppercase tracking-widest font-bold">Intro</span>
          <input
            type="text"
            value={intro}
            onChange={(e) => setIntro(e.target.value.slice(0, 400))}
            placeholder="A short one-liner shown above stop 1"
            className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-marigold"
            data-testid="admin-tour-intro"
          />
        </label>
      </div>

      <div className="space-y-3 mb-5">
        {stops.map((s, i) => (
          <div key={i} className="grid md:grid-cols-[140px_1fr] gap-3 p-3 border border-border rounded-xl" data-testid={`admin-tour-stop-${i}`}>
            <input
              type="text"
              value={s.term}
              onChange={(e) => setStop(i, "term", e.target.value.slice(0, 32))}
              placeholder={`stop ${i + 1} · term`}
              className="border border-border rounded-lg px-3 py-2 text-sm font-mono uppercase tracking-widest focus:outline-none focus:border-marigold"
              data-testid={`admin-tour-stop-term-${i}`}
            />
            <input
              type="text"
              value={s.caption}
              onChange={(e) => setStop(i, "caption", e.target.value.slice(0, 280))}
              placeholder="one sentence Ms. Pradhan would say when spotlighting this concept"
              className="border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-marigold"
              data-testid={`admin-tour-stop-caption-${i}`}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={publish}
          disabled={busy}
          className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="admin-tour-publish"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Publish this tour
        </button>
        <button
          onClick={revert}
          disabled={busy}
          className="btn-outline-ink text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="admin-tour-revert"
        >
          Revert to default
        </button>
        <div className="ml-auto text-xs text-ink2 font-mono">
          {tours.length} version{tours.length === 1 ? "" : "s"} in history
        </div>
      </div>

      <SeasonRibbonPanel />
    </div>
  );
}

function SeasonRibbonPanel() {
  const [ribbon, setRibbon] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/curriculum/season-ribbon")
      .then(({ data }) => {
        setRibbon(data && data.active ? data : null);
        setText(data && data.active ? (data.text || "") : "");
      })
      .catch(() => setRibbon(null));
  };
  useEffect(() => { load(); }, []);

  const publish = async () => {
    const t = text.trim();
    if (t.length < 2) {
      toast.error("Ribbon needs at least 2 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/season-ribbon", { text: t });
      toast.success("Ribbon is up.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't publish the ribbon.");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.post("/admin/season-ribbon/reset");
      toast.success("Ribbon taken down.");
      setText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't take it down.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-dashed border-ink/25" data-testid="admin-ribbon">
      <div className="flex items-baseline gap-3 flex-wrap mb-3">
        <h3 className="font-heading text-lg font-semibold text-ink">Season ribbon</h3>
        <span className="text-xs text-ink2/70 italic">A small marigold band at the top of the constellation.</span>
        <span className="ml-auto text-xs font-mono uppercase tracking-widest text-marigold" data-testid="admin-ribbon-status">
          {ribbon ? `LIVE · dressed by ${ribbon.curator}` : "not shown"}
        </span>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 80))}
          placeholder="e.g. Season 3 · Small ideas that scale"
          maxLength={80}
          className="flex-1 min-w-[260px] border border-border rounded-full px-4 py-2 text-sm font-heading focus:outline-none focus:border-marigold"
          data-testid="admin-ribbon-input"
        />
        <div className={`text-[10px] font-mono uppercase tracking-widest ${80 - text.length < 15 ? "text-marigold" : "text-ink2/60"}`}>
          {80 - text.length} left
        </div>
        <button
          onClick={publish}
          disabled={busy}
          className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="admin-ribbon-publish"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {ribbon ? "Replace ribbon" : "Hang ribbon"}
        </button>
        {ribbon && (
          <button
            onClick={clear}
            disabled={busy}
            className="btn-outline-ink text-xs disabled:opacity-50"
            data-testid="admin-ribbon-clear"
          >
            Take down
          </button>
        )}
      </div>
    </div>
  );
}
